import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { enqueue, subscribe, pending, isOnline, flush } from "@/lib/syncQueue";
import { Wifi, WifiOff, RefreshCw, CheckCircle2, AlertTriangle, LogIn, Camera, ListChecks, Package, MapPin } from "lucide-react";
import { toast } from "sonner";

export function SyncStatusBar() {
  const [, force] = useState(0);
  useEffect(() => subscribe(() => force((n) => n + 1)), []);
  const p = pending();
  const online = isOnline();
  const needsReview = p.filter((a) => a.needs_review).length;
  const failed = p.filter((a) => a.status === "failed").length;
  const waiting = p.filter((a) => a.status === "pending").length;
  let label = "Online · Synced";
  let cls = "bg-emerald-100 text-emerald-700";
  let Icon = Wifi;
  if (!online) { label = `Offline · ${waiting + failed} pending`; cls = "bg-slate-200 text-slate-700"; Icon = WifiOff; }
  else if (waiting) { label = `Syncing · ${waiting} pending`; cls = "bg-amber-100 text-amber-700"; Icon = RefreshCw; }
  else if (failed) { label = `Sync failed · ${failed}`; cls = "bg-rose-100 text-rose-700"; Icon = AlertTriangle; }
  else if (needsReview) { label = `${needsReview} need review`; cls = "bg-rose-100 text-rose-700"; Icon = AlertTriangle; }
  return (
    <div data-testid="sync-status-bar" className={`sticky top-0 z-20 flex items-center justify-between px-4 py-2 text-xs font-semibold ${cls}`}>
      <span className="flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" /> {label}</span>
      {online && (waiting + failed > 0) && <button onClick={flush} className="underline">Sync now</button>}
    </div>
  );
}

export function FieldPanel({ wo, onChange }) {
  const [note, setNote] = useState("");
  const [checklist, setChecklist] = useState([]);
  const [mat, setMat] = useState({ description: "", quantity: 1, cost: "" });
  const fileRef = useRef();
  const [phase, setPhase] = useState("before");
  const [, force] = useState(0);
  useEffect(() => subscribe(() => force((n) => n + 1)), []);
  useEffect(() => { api.get(`/contractor/work-orders/${wo.id}/checklist`).then(({ data }) => setChecklist(data.checklist)).catch(() => {}); }, [wo.id]);

  const addr = wo.property ? `${wo.property.street_address || ""} ${wo.property.city || ""} ${wo.property.state || ""}` : "";
  const navUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`;

  const act = (type, payload, msg) => { enqueue(type, wo.id, payload); toast.success(`${msg}${isOnline() ? "" : " (offline — will sync)"}`); onChange?.(); };
  const toggleItem = (key) => {
    const next = checklist.map((i) => i.key === key ? { ...i, done: !i.done } : i);
    setChecklist(next);
    enqueue("checklist", wo.id, { items: next.filter((i) => i.done).map((i) => ({ key: i.key, done: true })) });
  };
  const capture = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { enqueue("photo", wo.id, { dataUrl: reader.result, phase }, "photo"); toast.success(`Photo queued (${phase})${isOnline() ? "" : " — offline"}`); onChange?.(); };
    reader.readAsDataURL(file);
  };
  const complete = async () => {
    try { const { data } = await api.get(`/contractor/completion-check/${wo.id}`);
      if (!data.can_complete && isOnline()) return toast.error(`Cannot complete: ${data.missing.join(", ")}`);
    } catch { /* offline: allow local prep */ }
    act("complete", {}, isOnline() ? "Completing job" : "Completion prepared");
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4" data-testid="field-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-head font-semibold text-slate-900">Field Actions</h3>
        {addr.trim() && <a href={navUrl} target="_blank" rel="noreferrer" data-testid="navigate-btn" className="text-sm text-blue-600 flex items-center gap-1"><MapPin className="w-4 h-4" /> Navigate</a>}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Button data-testid="field-checkin" size="lg" variant="outline" onClick={() => act("checkin", {}, "Checked in")}><LogIn className="w-4 h-4 mr-1" /> Check In</Button>
        <Button data-testid="field-start" size="lg" variant="outline" onClick={() => act("start", {}, "Work started")}>Start Work</Button>
      </div>

      <div>
        <div className="text-xs font-bold uppercase text-slate-400 mb-2 flex items-center gap-1"><ListChecks className="w-3.5 h-3.5" /> Checklist</div>
        <div className="space-y-1">
          {checklist.map((i) => (
            <label key={i.key} data-testid={`chk-${i.key}`} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={i.done} onChange={() => toggleItem(i.key)} />
              <span className={i.done ? "line-through text-slate-400" : ""}>{i.label}{i.required && <span className="text-rose-500"> *</span>}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <div className="text-xs font-bold uppercase text-slate-400 mb-2 flex items-center gap-1"><Camera className="w-3.5 h-3.5" /> Photos</div>
        <div className="flex gap-2">
          {["before", "progress", "after"].map((ph) => (
            <Button key={ph} size="sm" variant={phase === ph ? "default" : "outline"} className={phase === ph ? "bg-blue-600" : ""} onClick={() => { setPhase(ph); setTimeout(() => fileRef.current?.click(), 50); }} data-testid={`photo-${ph}`}>{ph}</Button>
          ))}
          <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={capture} data-testid="field-photo-input" />
        </div>
      </div>

      <div>
        <div className="text-xs font-bold uppercase text-slate-400 mb-2 flex items-center gap-1"><Package className="w-3.5 h-3.5" /> Add Material</div>
        <div className="grid grid-cols-3 gap-2">
          <Input placeholder="Description" value={mat.description} onChange={(e) => setMat({ ...mat, description: e.target.value })} data-testid="mat-desc" className="col-span-3" />
          <Input type="number" placeholder="Qty" value={mat.quantity} onChange={(e) => setMat({ ...mat, quantity: e.target.value })} />
          <Input type="number" placeholder="Cost" value={mat.cost} onChange={(e) => setMat({ ...mat, cost: e.target.value })} />
          <Button data-testid="mat-add" onClick={() => { if (!mat.description) return toast.error("Description required"); act("material", { ...mat, cost: parseFloat(mat.cost || 0) }, "Material added"); setMat({ description: "", quantity: 1, cost: "" }); }}>Add</Button>
        </div>
      </div>

      <div>
        <Textarea placeholder="Add a note…" value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="field-note-text" />
        <Button size="sm" className="mt-2 bg-blue-600 hover:bg-blue-700" data-testid="field-note-add" onClick={() => { if (!note.trim()) return; act("note", { text: note }, "Note added"); setNote(""); }}>Add Note</Button>
      </div>

      <Button data-testid="field-complete" size="lg" className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={complete}><CheckCircle2 className="w-4 h-4 mr-1" /> Complete Job</Button>
    </div>
  );
}
